import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { UserCheck, RefreshCw, Check, X, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };

const errMsg = (e, fb) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(' ');
  return fb || 'Something went wrong';
};

const reasonLabel = (r) => {
  if (r === 'inactivity_30_days') return 'Inactivity (30+ days)';
  if (r === 'manual') return 'Deactivated by admin';
  return r || '—';
};

export const CrmReactivationRequests = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [busyId, setBusyId] = useState('');
  const [denyTarget, setDenyTarget] = useState(null);
  const [denyNote, setDenyNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/reactivation-requests`, { ...cfg, params: { status: statusFilter } });
      setRows(data.requests || []);
    } catch (er) { toast.error(errMsg(er)); } finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    setBusyId(r.request_id);
    try {
      await axios.post(`${API}/api/admin/reactivation-requests/${r.request_id}/approve`, {}, cfg);
      toast.success(`${r.employee_name} reactivated`);
      await load();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusyId(''); }
  };

  const deny = async () => {
    const r = denyTarget;
    setBusyId(r.request_id); setDenyTarget(null);
    try {
      await axios.post(`${API}/api/admin/reactivation-requests/${r.request_id}/deny`, { note: denyNote }, cfg);
      toast.success('Request denied'); setDenyNote('');
      await load();
    } catch (er) { toast.error(errMsg(er)); } finally { setBusyId(''); }
  };

  const statusBadge = (s) => {
    if (s === 'pending') return <Badge className="bg-amber-100 text-amber-700">Pending</Badge>;
    if (s === 'approved') return <Badge className="bg-green-100 text-green-700">Approved</Badge>;
    if (s === 'denied') return <Badge className="bg-gray-200 text-gray-700">Denied</Badge>;
    return <Badge variant="outline">{s}</Badge>;
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="crm-reactivation-requests">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-2xl bg-[#06D6A0]/10 flex items-center justify-center"><UserCheck className="w-6 h-6 text-[#06D6A0]" /></div>
        <div>
          <h1 className="text-2xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Reactivation Requests</h1>
          <p className="text-sm text-[#6B6B7A]">Deactivated employees asking to be let back in — approve with one tap.</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {[['pending', 'Pending'], ['approved', 'Approved'], ['denied', 'Denied'], ['all', 'All']].map(([v, label]) => (
          <button
            key={v}
            onClick={() => setStatusFilter(v)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${statusFilter === v ? 'bg-[#1E1E24] text-white' : 'bg-white border border-[#E8E5DF] text-[#6B6B7A] hover:bg-[#FFFDF8]'}`}
            data-testid={`rr-filter-${v}`}
          >
            {label}
          </button>
        ))}
        <div className="flex-1" />
        <Button variant="outline" onClick={load} className="rounded-full" data-testid="rr-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>

      <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Employee</TableHead><TableHead>Deactivation reason</TableHead><TableHead>Note</TableHead>
            <TableHead>Requested</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#6B6B7A]">Loading…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#6B6B7A]">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No {statusFilter === 'all' ? '' : statusFilter} reactivation requests.
              </TableCell></TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.request_id} data-testid={`rr-row-${r.request_id}`}>
                <TableCell>
                  <div className="font-medium text-[#1E1E24]">{r.employee_name}</div>
                  <div className="text-xs text-[#6B6B7A]">{r.employee_email}{r.employee_number ? ` · ${r.employee_number}` : ''}</div>
                </TableCell>
                <TableCell className="text-sm">{reasonLabel(r.reason)}</TableCell>
                <TableCell className="text-xs text-[#6B6B7A] max-w-[220px] truncate" title={r.note || ''}>{r.note || '—'}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</TableCell>
                <TableCell>{statusBadge(r.status)}</TableCell>
                <TableCell className="text-right">
                  {r.status === 'pending' ? (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" onClick={() => approve(r)} disabled={busyId === r.request_id} data-testid={`rr-approve-${r.request_id}`} className="rounded-full bg-[#06D6A0] hover:bg-[#05B88A] text-white"><Check className="w-3 h-3 mr-1" /> Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => setDenyTarget(r)} disabled={busyId === r.request_id} data-testid={`rr-deny-${r.request_id}`} className="rounded-full border-red-300 text-red-600 hover:bg-red-50"><X className="w-3 h-3 mr-1" /> Deny</Button>
                    </div>
                  ) : (
                    <span className="text-xs text-[#9CA3AF]">{r.resolved_by_name ? `by ${r.resolved_by_name}` : '—'}</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!denyTarget} onOpenChange={(o) => { if (!o) { setDenyTarget(null); setDenyNote(''); } }}>
        <DialogContent className="max-w-md bg-white" data-testid="rr-deny-dialog">
          <DialogHeader>
            <DialogTitle>Deny reactivation for {denyTarget?.employee_name}?</DialogTitle>
            <DialogDescription>They'll stay deactivated. You can add an optional note for the record.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Note (optional)</Label>
            <Input value={denyNote} onChange={(e) => setDenyNote(e.target.value)} data-testid="rr-deny-note" className="mt-1 rounded-xl" placeholder="e.g. No longer employed" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDenyTarget(null); setDenyNote(''); }} className="rounded-full">Cancel</Button>
            <Button onClick={deny} data-testid="rr-deny-confirm" className="rounded-full bg-[#FF3B6B] text-white">Deny Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CrmReactivationRequests;
